import React from 'react';
import type { RoleScopeRow, ScopeOwnership, OfferDepartment } from '@ooh/contracts';
import { bundlePartnersOf } from './grant-helpers';
import { GrantRow, type MediaTypeOption } from './grant-row';

export interface PermissionDef {
  id: number;
  code: string;
  name: string;
}

export interface PermissionGrantListProps {
  /** Ordered resource keys (e.g. ['media_unit', 'reservation', ...]) — data-driven, supplied by the caller. */
  resourceOrder: string[];
  permissionsByResource: Record<string, PermissionDef[]>;
  /** Translated section heading per resource key (e.g. { media_unit: 'Nośniki' }). */
  sectionLabels: Record<string, string>;
  /** All grant rows for the currently selected role. */
  grants: RoleScopeRow[];
  disabled: boolean;
  /** Predicate controlling whether the department-scope chip is interactive for a given permission code. */
  allowDepartmentScope: (permissionCode: string) => boolean;
  /** Predicate controlling whether the media-type scope chip is interactive for a given permission code. */
  allowMediaTypeScope: (permissionCode: string) => boolean;
  mediaTypes: MediaTypeOption[];
  /** `groupIndex` addresses one of the permission's independent scope groups — see `groupGrantsByScope`. */
  onChangeMediaTypes: (
    permissionCode: string,
    groupIndex: number,
    mediaTypeCodes: string[] | null,
  ) => void;
  onChangeOwnership: (
    permissionCode: string,
    groupIndex: number,
    ownership: ScopeOwnership | null,
  ) => void;
  onChangeDepartment: (
    permissionCode: string,
    groupIndex: number,
    department: OfferDepartment | null,
  ) => void;
  /** Adds a scope group — the initial grant when ungranted, "Dodaj zakres" once already granted. */
  onAddScope: (permissionCode: string) => void;
  /** Removes one scope group; removing the last one revokes the permission entirely. */
  onRemoveScope: (permissionCode: string, groupIndex: number) => void;
}

/**
 * Renders every permission for the selected role, grouped into resource
 * sections (Nośniki, Rezerwacje, Oferty, ...) inside one bordered container.
 */
export function PermissionGrantList({
  resourceOrder,
  permissionsByResource,
  sectionLabels,
  grants,
  disabled,
  allowDepartmentScope,
  allowMediaTypeScope,
  mediaTypes,
  onChangeMediaTypes,
  onChangeOwnership,
  onChangeDepartment,
  onAddScope,
  onRemoveScope,
}: PermissionGrantListProps) {
  // Computed once per render (not per row) so `bundlePartnersOf` — a pure
  // PERMISSION_BUNDLES lookup — isn't recomputed unnecessarily per permission.
  const grantedCodes = new Set(grants.map((g) => g.permissionCode));

  return (
    <div className="rounded-lg border border-border/50">
      {resourceOrder.map((resource, sectionIndex) => {
        const perms = permissionsByResource[resource] ?? [];
        return (
          <div key={resource} className={sectionIndex === 0 ? '' : 'border-t border-border/40'}>
            <p className="px-5 pt-3.5 pb-1 text-2xs font-semibold uppercase tracking-wider text-muted-foreground/80">
              {sectionLabels[resource] ?? resource}
            </p>
            <div className="px-5 pb-3">
              {perms.map((perm) => (
                <GrantRow
                  key={perm.id}
                  permissionCode={perm.code}
                  permissionName={perm.name}
                  grants={grants.filter((g) => g.permissionCode === perm.code)}
                  disabled={disabled}
                  departmentScopeEditable={allowDepartmentScope(perm.code)}
                  mediaTypeScopeEditable={allowMediaTypeScope(perm.code)}
                  mediaTypes={mediaTypes}
                  onChangeMediaTypes={(groupIndex, mts) =>
                    onChangeMediaTypes(perm.code, groupIndex, mts)
                  }
                  onChangeOwnership={(groupIndex, o) => onChangeOwnership(perm.code, groupIndex, o)}
                  onChangeDepartment={(groupIndex, d) =>
                    onChangeDepartment(perm.code, groupIndex, d)
                  }
                  onAddScope={() => onAddScope(perm.code)}
                  onRemoveScope={(groupIndex) => onRemoveScope(perm.code, groupIndex)}
                  pairedWithCodes={bundlePartnersOf(perm.code).filter((code) =>
                    grantedCodes.has(code),
                  )}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
